import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { formatDate } from '../../common/SharedFunctions';
import { BfiDataGrid, Column, MasterDetail } from '../../common/SharedComponents';
import { NoticesApi } from '@apply/api';
import { applicationInsights } from '../../ApplicationInsights';
import { UserNoticeSubSectionTypeOptions } from '@apply/constants';
import PortfolioTable from './PortfolioTable';
import { AddNoticeButton } from '../common/NoticeComponents';
import { Tooltip } from 'devextreme-react/tooltip';
import { GridSettingType } from '@apply/constants';
import { ViewNoticeButton } from '../common/NoticeComponents';
const NoticesSearchTable = ({ subSection = UserNoticeSubSectionTypeOptions.Open, gridStore }) => {
    const searchState = useSelector((state) => state.searchState);
    const noticesApi = new NoticesApi.NoticesApi(applicationInsights);
    const [dataStore, set_dataStore] = useState(gridStore?.store);
    const [isClientHeaderVisible, set_isClientHeaderVisible] = useState(false);
    const [isPropertyHeaderVisible, set_isPropertyHeaderVisible] = useState(false);

    const styles = {
        red: { color: 'red' },
        black: { color: 'black' },
    };

    const dataFunction = (criteria) => {
        criteria.subSectionId = subSection.subSectionId;
        if (!criteria.sorting || criteria.sorting.column === 'id') {
            criteria.sorting = {
                column: 'createdAt',
                direction: 'asc',
            };
        }
        return noticesApi.getGrid(criteria);
    };

    useEffect(() => {
        if (gridStore) {
            gridStore.setOnLoad('id', dataFunction, searchState?.searchText?.notices);
            set_dataStore(gridStore.store);
        }
    }, [gridStore]);

    const appliedDateCellRender = (e) => {
        return (
            <>
                {!e.data.createdAt && 'Not Set'}
                {e.data.createdAt && formatDate(e.data.createdAt)}{' '}
                {e.data.daysSinceApplied > 0 && (
                    <span style={e.data.daysSinceApplied > 30 ? styles.red : styles.black}> +{e.data.daysSinceApplied}</span>
                )}
            </>
        );
    };

    const onPropertyStreetRender = (e) => {
        if (e.data.isPortfolio) {
            return <i>Multiple Properties</i>;
        }

        const targetId = `ntPropertyAddress_${e.data.id}`;
        return (
            <span key={'noticeProperty' + e.data.id}>
                <div
                    style={{ fontSize: '13px', cursor: 'default' }}
                    data-tip
                    data-for={targetId}
                >
                    {e.data.propertyStreet1} {e.data.propertyStreet2}
                </div>
                <Tooltip
                    target={`[data-for="${targetId}"]`}
                    showEvent='mouseenter'
                    hideEvent='mouseleave'
                    hideOnOutsideClick={false}
                >
                    {e.data.propertyStreet1} {e.data.propertyStreet2}
                    <br />
                    {e.data.propertyCity} {e.data.propertyState}, {e.data.propertyZip}
                </Tooltip>
            </span>
        );
    };

    const onPropertyCityRender = (e) => {
        if (e.data.isPortfolio) {
            return null;
        }

        return e.data.propertyCity;
    };

    const onPropertyStateRender = (e) => {
        if (e.data.isPortfolio) {
            return null;
        }

        return e.data.propertyState;
    };

    const onNoticeRender = (e) => {
        if (!e.value) {
            return null;
        }

        return e.value.map((n, index) => {
            if (!n.id) {
                if (n.isActive) {
                    return (
                        <AddNoticeButton
                            key={'notice' + index}
                            notice={n}
                        />
                    );
                } else {
                    const targetId = `nt_${n.noticeTypeId}_${e.data.id}`;
                    return (
                        <span key={'notice' + index}>
                            <div
                                style={{ fontSize: '13px', cursor: 'default' }}
                                data-tip
                                data-for={targetId}
                            >
                                {n.title}
                            </div>
                            {n.tooltip && (
                                <Tooltip
                                    target={`[data-for="${targetId}"]`}
                                    showEvent='mouseenter'
                                    hideEvent='mouseleave'
                                    hideOnOutsideClick={false}
                                >
                                    {n.tooltip}
                                </Tooltip>
                            )}
                        </span>
                    );
                }
            } else
                return (
                    <div key={'notice' + index}>
                        <ViewNoticeButton notice={n} />
                    </div>
                );
        });
    };

    const onDateRender = (e) => {
        if (!e.value) {
            return null;
        }
        return formatDate(new Date(e.value));
    };

    const onRenderPortfolioGrid = (e) => {
        if (!e.data.isPortfolio) {
            return null;
        }
        return (
            <div style={{ padding: '5px 20px' }}>
                <PortfolioTable loans={e.data.portfolio} />
            </div>
        );
    };

    const onRowPrepared = (input) => {
        if (input?.rowType === 'data') {
            if (!input.data.isPortfolio) {
                if (input.cells && input.cells.length > 0 && input.cells[0]?.cellElement) {
                    input.cells[0].cellElement.classList.remove('dx-datagrid-closed');
                    input.cells[0].cellElement.classList.remove('dx-datagrid-expand');
                    input.cells[0].cellElement.childNodes[0].classList.remove('dx-datagrid-group-closed');
                    input.cells[0].cellElement.childNodes[0].classList.remove('dx-datagrid-group-opened');
                }
            }
        }
    };

    const onCellPrepared = (e) => {
        if (e.rowType === 'header') {
            if (
                e.column.name === 'appNotices' ||
                e.column.name === 'floodNotices' ||
                e.column.name === 'appraisalReports' ||
                e.column.name === 'appraisalNotices' ||
                e.column.name === 'postClosingCertNotices'
            ) {
                e.cellElement.style.textAlign = 'Center';
                e.cellElement.style.fontWeight = 'bold';
                e.cellElement.style.color = 'black';
            }

            if (e.column.name === 'noticesToEmail') {
                e.cellElement.title =
                    'The address at which the borrower received notices. After the first notice is sent for a loan, subsequent notices for that loan must be to the same address (any additional requested by borrower to be handled outside this system). Address set in borrower principal profile and selected at borrower company level.';
            }

            if (e.column.name === 'PropertyDetailsHeader' || e.column.name === 'ClientDetailsHeader') {
                e.cellElement.style.color = '#0000EE';
            }
        }
    };

    const onCellClick = (e) => {
        if (e.rowType === 'header') {
            if (e.column.name === 'PropertyDetailsHeader') {
                set_isPropertyHeaderVisible(!isPropertyHeaderVisible);
            }
            if (e.column.name === 'ClientDetailsHeader') {
                set_isClientHeaderVisible(!isClientHeaderVisible);
            }
        }
    };

    return (
        <BfiDataGrid
            gridType={GridSettingType.Notices}
            dataStore={dataStore}
            applicationInsights={applicationInsights}
            onRowPrepared={onRowPrepared}
            onCellPrepared={onCellPrepared}
            onCellClick={onCellClick}
            allowColumnResizing={true}
            height={'100%'}
            wordWrapEnabled={true}
        >
            <Column
                dataField='bfiNumber'
                caption='BFI #'
                width={120}
            />
            <Column
                dataField='borrowerEntity'
                caption='Entity'
                minWidth={120}
            />
            <Column
                alignment={'left'}
                name='PropertyDetailsHeader'
                caption={isPropertyHeaderVisible ? 'Property -' : 'Property +'}
                allowSorting={false}
            >
                <Column
                    dataField='propertyStreet1'
                    caption='Street'
                    width={200}
                    cellRender={onPropertyStreetRender}
                    allowReordering={false}
                />
                <Column
                    dataField='propertyCity'
                    caption='City'
                    width={100}
                    cellRender={onPropertyCityRender}
                    visible={isPropertyHeaderVisible}
                    allowReordering={false}
                />
                <Column
                    dataField='propertyState'
                    caption='State'
                    width={50}
                    cellRender={onPropertyStateRender}
                    visible={isPropertyHeaderVisible}
                    allowReordering={false}
                />
            </Column>
            <Column
                dataField='description'
                caption='Status'
                width={190}
                allowSorting={false}
            />
            <Column
                dataField='createdAt'
                caption='Applied'
                cellRender={appliedDateCellRender}
                width={120}
                allowSorting={true}
            />
            <Column
                dataField='poc'
                caption='POC'
                width={100}
            />
            <Column
                alignment={'left'}
                name='ClientDetailsHeader'
                caption={isClientHeaderVisible ? 'Client -' : 'Client +'}
                allowSorting={false}
            >
                <Column
                    dataField='noticesToName'
                    caption='User'
                    width={150}
                    allowReordering={false}
                />
                <Column
                    dataField='noticesToEmail'
                    caption='Address (i)'
                    width={200}
                    visible={isClientHeaderVisible}
                    allowReordering={false}
                />
                <Column
                    dataField='noticesToESign'
                    caption='E-Sign'
                    cellRender={onDateRender}
                    width={60}
                    visible={isClientHeaderVisible}
                    allowReordering={false}
                />
            </Column>
            <Column
                dataField='appraisalNotices'
                caption='Appraisal Notice'
                cellRender={onNoticeRender}
                allowSorting={false}
                allowReordering={false}
                width={100}
            />
            <Column
                dataField='appraisalReports'
                caption='Appraisal Report'
                cellRender={onNoticeRender}
                allowSorting={false}
                allowReordering={false}
                width={100}
            />
            <Column
                dataField='floodNotices'
                caption='Flood Notice'
                cellRender={onNoticeRender}
                allowSorting={false}
                allowReordering={false}
                width={100}
            />
            <Column
                dataField='appNotices'
                caption='Application Notice'
                cellRender={onNoticeRender}
                allowSorting={false}
                allowReordering={false}
                width={100}
            />
            <Column
                dataField='postClosingCertNotices'
                caption='Post Close Cert. Notice'
                cellRender={onNoticeRender}
                allowSorting={false}
                allowReordering={false}
                width={100}
            />
            <MasterDetail
                enabled={true}
                render={onRenderPortfolioGrid}
            />
        </BfiDataGrid>
    );
};

export default NoticesSearchTable;
